import React, {
  forwardRef,
  useMemo,
  useRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useEffect,
  type CSSProperties,
  type HTMLAttributes,
} from "react"
import { flattenStyle } from "./style"
import { filterDomProps } from "./dom-props"
import { WebText } from "./web-text"
import { resolveImageSrc, type ImageSrcInput } from "./image-source"

export type TextStyle = CSSProperties
export type ViewStyle = CSSProperties
export type ImageStyle = CSSProperties
export type StyleProp<T> = T | T[] | false | null | undefined

/** H5 兼容 RN 事件/列表类型（仅作类型用） */
export type NativeSyntheticEvent<T> = { nativeEvent: T }
export type TextInputSelectionChangeEventData = {
  selection: { start: number; end: number }
}
export type TextInputContentSizeChangeEventData = {
  contentSize: { width: number; height: number }
}
export type ListRenderItemInfo<T> = { item: T; index: number }
export type FlatListProps<T = unknown> = {
  data?: T[]
  renderItem?: (info: ListRenderItemInfo<T>) => React.ReactNode
  keyExtractor?: (item: T, index: number) => string
  style?: unknown
  contentContainerStyle?: unknown
  onEndReached?: () => void
  onEndReachedThreshold?: number
  inverted?: boolean
  horizontal?: boolean
  extraData?: unknown
  [key: string]: unknown
}

type BaseProps = {
  style?: unknown
  children?: React.ReactNode
  pointerEvents?: "none" | "auto" | "box-none" | "box-only"
  testID?: string
  accessible?: boolean
  accessibilityLabel?: string
  onLayout?: (e: { nativeEvent: { layout: { width: number; height: number; x: number; y: number } } }) => void
  collapsable?: boolean
}

function useDomStyle(style: unknown, extra?: CSSProperties): CSSProperties {
  return useMemo(
    () => ({
      boxSizing: "border-box",
      ...extra,
      ...flattenStyle(style as never),
    }),
    [style, extra],
  )
}

function useLayoutRef<T extends HTMLElement>(ref: React.Ref<T>, onLayout?: BaseProps["onLayout"]) {
  const onLayoutRef = useRef(onLayout)
  onLayoutRef.current = onLayout
  const lastLayoutKey = useRef("")

  return useCallback(
    (node: T | null) => {
      if (typeof ref === "function") ref(node)
      else if (ref) (ref as React.MutableRefObject<T | null>).current = node

      if (!node || !onLayoutRef.current) return

      const rect = node.getBoundingClientRect()
      const key = `${rect.width}|${rect.height}|${rect.left}|${rect.top}`
      if (lastLayoutKey.current === key) return
      lastLayoutKey.current = key

      onLayoutRef.current({
        nativeEvent: {
          layout: { width: rect.width, height: rect.height, x: rect.left, y: rect.top },
        },
      })
    },
    [ref],
  )
}

export const View = forwardRef<
  HTMLDivElement,
  BaseProps &
    HTMLAttributes<HTMLDivElement> & {
      onPress?: React.MouseEventHandler<HTMLDivElement>
      onClick?: React.MouseEventHandler<HTMLDivElement>
      onLongPress?: () => void
    }
>(function View(
  { style, children, onLayout, pointerEvents, testID, onPress, onClick, onLongPress, ...props },
  ref,
) {
  const layoutRef = useLayoutRef(ref, onLayout)
  const handleClick = onClick ?? onPress
  const domStyle = useDomStyle(style, {
    ...(pointerEvents ? { pointerEvents } : undefined),
    ...(handleClick || onLongPress ? { cursor: "pointer" } : undefined),
  })
  return (
    <div
      className="rn-view"
      ref={layoutRef}
      style={domStyle}
      data-testid={testID}
      onClick={handleClick}
      onContextMenu={
        onLongPress
          ? (e) => {
              e.preventDefault()
              onLongPress()
            }
          : undefined
      }
      role={handleClick ? "button" : undefined}
      tabIndex={handleClick ? 0 : undefined}
      onKeyDown={
        handleClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleClick(e)
              }
            }
          : undefined
      }
      {...(filterDomProps(props) as HTMLAttributes<HTMLDivElement>)}
    >
      {children}
    </div>
  )
})

export const Text = forwardRef<
  HTMLSpanElement,
  BaseProps & HTMLAttributes<HTMLSpanElement> & { numberOfLines?: number; ellipsizeMode?: string }
>(function Text({ style, children, numberOfLines, ellipsizeMode, onLayout, testID, ...props }, ref) {
  const layoutRef = useLayoutRef(ref, onLayout)
  return (
    <WebText
      ref={layoutRef}
      style={useDomStyle(style)}
      numberOfLines={numberOfLines}
      ellipsizeMode={ellipsizeMode}
      testID={testID}
      {...(filterDomProps(props) as HTMLAttributes<HTMLSpanElement>)}
    >
      {children}
    </WebText>
  )
})

export const Pressable = forwardRef<
  HTMLDivElement,
  BaseProps &
    Omit<HTMLAttributes<HTMLDivElement>, "onClick"> & {
      onPress?: React.MouseEventHandler<HTMLDivElement>
      onClick?: React.MouseEventHandler<HTMLDivElement>
      onLongPress?: () => void
      disabled?: boolean
      activeOpacity?: number
    }
>(function Pressable(
  {
    style,
    children,
    onPress,
    onClick,
    onLongPress,
    disabled,
    activeOpacity: _activeOpacity,
    onLayout,
    testID,
    ...props
  },
  ref,
) {
  const layoutRef = useLayoutRef(ref, onLayout)
  const handleClick = onClick ?? onPress
  const domStyle = useDomStyle(style, {
    cursor: disabled ? "not-allowed" : handleClick || onLongPress ? "pointer" : undefined,
    ...(disabled ? { opacity: 0.5, pointerEvents: "none" as const } : undefined),
  })
  return (
    <div
      className="rn-pressable"
      ref={layoutRef}
      style={domStyle}
      role={handleClick || onLongPress ? "button" : undefined}
      tabIndex={handleClick || onLongPress ? (disabled ? -1 : 0) : undefined}
      aria-disabled={disabled || undefined}
      data-testid={testID}
      onClick={disabled ? undefined : handleClick}
      onContextMenu={
        onLongPress && !disabled
          ? (e) => {
              e.preventDefault()
              onLongPress()
            }
          : undefined
      }
      onKeyDown={
        handleClick && !disabled
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleClick(e)
              }
            }
          : undefined
      }
      {...(filterDomProps(props) as HTMLAttributes<HTMLDivElement>)}
    >
      {children}
    </div>
  )
})

export const TouchableOpacity = Pressable

export const TouchableWithoutFeedback = Pressable

export const TouchableHighlight = Pressable

export const ScrollView = forwardRef<
  HTMLDivElement,
  BaseProps &
    HTMLAttributes<HTMLDivElement> & {
      horizontal?: boolean
      contentContainerStyle?: unknown
      keyboardShouldPersistTaps?: string
      showsVerticalScrollIndicator?: boolean
      showsHorizontalScrollIndicator?: boolean
    }
>(function ScrollView(
  {
    style,
    contentContainerStyle,
    horizontal,
    children,
    onLayout,
    testID,
    keyboardShouldPersistTaps: _keyboardShouldPersistTaps,
    showsVerticalScrollIndicator: _showsVerticalScrollIndicator,
    showsHorizontalScrollIndicator: _showsHorizontalScrollIndicator,
    keyboardDismissMode: _keyboardDismissMode,
    nestedScrollEnabled: _nestedScrollEnabled,
    ...props
  },
  ref,
) {
  const layoutRef = useLayoutRef(ref, onLayout)
  const domStyle = useDomStyle(style, {
    WebkitOverflowScrolling: "touch",
    minHeight: 0,
    boxSizing: "border-box",
    ...(horizontal ? {} : { width: "100%" }),
  })
  const contentStyle = useDomStyle(contentContainerStyle, { flexGrow: 0 })
  return (
    <div
      className={horizontal ? "rn-scroll-view rn-scroll-view--horizontal" : "rn-scroll-view"}
      ref={layoutRef}
      style={domStyle}
      data-testid={testID}
      {...(filterDomProps(props) as HTMLAttributes<HTMLDivElement>)}
    >
      <div style={contentStyle}>{children}</div>
    </div>
  )
})

function mapTextContentTypeToAutoComplete(textContentType?: string): string | undefined {
  if (!textContentType) return undefined
  const map: Record<string, string> = {
    password: "current-password",
    newPassword: "new-password",
    username: "username",
    emailAddress: "email",
    telephoneNumber: "tel",
    oneTimeCode: "one-time-code",
    none: "off",
  }
  return map[textContentType]
}

function mapKeyboardTypeToAutoComplete(keyboardType?: string): string | undefined {
  if (!keyboardType) return undefined
  if (keyboardType === "email-address") return "email"
  if (keyboardType === "phone-pad" || keyboardType === "numeric") return "tel"
  return undefined
}

export type TextInputRef = HTMLInputElement | HTMLTextAreaElement

export const TextInput = forwardRef<
  TextInputRef,
  BaseProps & {
    value?: string
    defaultValue?: string
    placeholder?: string
    secureTextEntry?: boolean
    multiline?: boolean
    editable?: boolean
    autoFocus?: boolean
    onChangeText?: (text: string) => void
    onSubmitEditing?: () => void
    onKeyPress?: (e: NativeSyntheticEvent<{ key: string }>) => void
    onSelectionChange?: (e: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => void
    onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    keyboardType?: string
    autoCapitalize?: string
    autoCorrect?: boolean
    maxLength?: number
    placeholderTextColor?: string
    textContentType?: string
    autoComplete?: string
    name?: string
    error?: unknown
    isFocus?: boolean
    label?: string
    blurOnSubmit?: boolean
    textAlignVertical?: string
    includeFontPadding?: boolean
    underlineColorAndroid?: string
    numberOfLines?: number
  }
>(function TextInput(
  {
    style,
    value,
    defaultValue,
    placeholder,
    secureTextEntry,
    multiline,
    editable = true,
    onChangeText,
    onSubmitEditing,
    testID,
    placeholderTextColor,
    keyboardType,
    autoCapitalize,
    autoCorrect,
    textContentType,
    autoComplete,
    name,
    caretHidden: _caretHidden,
    selectionColor: _selectionColor,
    selectTextOnFocus: _selectTextOnFocus,
    showSoftInputOnFocus: _showSoftInputOnFocus,
    contextMenuHidden: _contextMenuHidden,
    returnKeyType: _returnKeyType,
    blurOnSubmit: _blurOnSubmit,
    clearButtonMode: _clearButtonMode,
    importantForAutofill: _importantForAutofill,
    enablesReturnKeyAutomatically: _enablesReturnKeyAutomatically,
    underlineColorAndroid: _underlineColorAndroid,
    includeFontPadding: _includeFontPadding,
    submitBehavior: _submitBehavior,
    returnKeyLabel: _returnKeyLabel,
    textAlignVertical,
    numberOfLines,
    onKeyPress,
    onSelectionChange,
    error: _error,
    isFocus: _isFocus,
    label: _label,
    ...props
  },
  ref,
) {
  const htmlAutoComplete =
    autoComplete ??
    mapTextContentTypeToAutoComplete(textContentType) ??
    (secureTextEntry ? "current-password" : mapKeyboardTypeToAutoComplete(keyboardType))

  const domStyle = useDomStyle(style, {
    border: "none",
    outline: "none",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    resize: multiline ? "none" : undefined,
    color: "inherit",
    ...(textAlignVertical === "top" ? { verticalAlign: "top" } : undefined),
  })

  const handleSelectionChange = (
    e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (!onSelectionChange) return
    const target = e.currentTarget
    onSelectionChange({
      nativeEvent: {
        selection: {
          start: target.selectionStart ?? 0,
          end: target.selectionEnd ?? 0,
        },
      },
    } as never)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onKeyPress?.({ nativeEvent: { key: e.key } } as never)
    if (e.key === "Enter" && !e.shiftKey && multiline) {
      e.preventDefault()
      onSubmitEditing?.()
      return
    }
    if (e.key === "Enter" && !multiline) onSubmitEditing?.()
  }

  const shared = {
    value,
    defaultValue,
    placeholder,
    disabled: editable === false,
    name,
    autoComplete: htmlAutoComplete,
    autoCapitalize: autoCapitalize as React.HTMLAttributes<HTMLInputElement>["autoCapitalize"],
    spellCheck: autoCorrect === undefined ? undefined : autoCorrect,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChangeText?.(e.target.value),
    onSelect: handleSelectionChange,
    onKeyDown: handleKeyDown,
    style: domStyle,
    "data-testid": testID,
    ...(placeholderTextColor ? { style: { ...domStyle, color: undefined } } : {}),
  }

  if (multiline) {
    return (
      <textarea
        ref={ref as React.Ref<HTMLTextAreaElement>}
        {...shared}
        rows={numberOfLines ?? 1}
        {...(filterDomProps(props) as HTMLAttributes<HTMLTextAreaElement>)}
      />
    )
  }
  return (
    <input
      ref={ref as React.Ref<HTMLInputElement>}
      type={secureTextEntry ? "password" : "text"}
      {...shared}
      {...(filterDomProps(props) as HTMLAttributes<HTMLInputElement>)}
    />
  )
})

function spinnerTrackColor(color: string): string {
  if (!color.startsWith("#")) {
    return `color-mix(in srgb, ${color} 20%, transparent)`
  }
  let hex = color.slice(1)
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("")
  }
  if (hex.length === 6) {
    return `#${hex}33`
  }
  return color
}

export function ActivityIndicator({
  size = "small",
  color = "#999",
  style,
}: {
  size?: number | "small" | "large"
  color?: string
  style?: unknown
}) {
  const px = size === "large" ? 36 : size === "small" ? 20 : size
  const track = spinnerTrackColor(color)
  const domStyle = useDomStyle(style, {
    display: "inline-block",
    flexShrink: 0,
    width: px,
    height: px,
    border: `2px solid ${track}`,
    borderTopColor: color,
    borderRadius: "50%",
    animation: "chatredapp-spin 0.8s linear infinite",
    alignSelf: "center",
  })
  return <div className="rn-activity-indicator" style={domStyle} role="progressbar" aria-busy="true" />
}

type ImageSource = ImageSrcInput

function getImageHref(src?: ImageSrcInput, legacySource?: ImageSrcInput): string {
  return resolveImageSrc(src, legacySource)
}

export type ResolvedAssetSource = {
  uri: string
  width: number
  height: number
  scale: number
}

/** 兼容 RN Image.resolveAssetSource（Vite 静态资源为 URL 字符串） */
export function resolveAssetSource(source: unknown): ResolvedAssetSource {
  const uri = resolveImageSrc(source as ImageSrcInput)
  if (!uri) return { uri: "", width: 0, height: 0, scale: 1 }
  if (source && typeof source === "object" && !Array.isArray(source)) {
    const record = source as { width?: number; height?: number }
    return {
      uri,
      width: record.width ?? 0,
      height: record.height ?? 0,
      scale: 1,
    }
  }
  return { uri, width: 0, height: 0, scale: 1 }
}

export function getImageSize(
  uri: string,
  success: (width: number, height: number) => void,
  failure?: (error: Error) => void,
) {
  if (typeof window === "undefined") {
    success(0, 0)
    return
  }
  const img = new window.Image()
  img.onload = () => success(img.naturalWidth, img.naturalHeight)
  img.onerror = () => failure?.(new Error(`Failed to get size for ${uri}`))
  img.src = resolveAssetSource(uri).uri
}

export const Image = Object.assign(
  forwardRef<
    HTMLImageElement,
    BaseProps & {
      src?: ImageSrcInput
      /** @deprecated 请使用 src */
      source?: ImageSrcInput
      resizeMode?: "cover" | "contain" | "stretch" | "center"
      onLoadEnd?: () => void
      onError?: () => void
    }
  >(function Image({ src, source, style, resizeMode = "cover", onLoadEnd, onError, testID, ...props }, ref) {
    const domStyle = useDomStyle(style, { objectFit: resizeMode })
    const href = getImageHref(src, source)
    return (
      <img
        ref={ref}
        src={href}
        alt=""
        style={domStyle}
        onLoad={onLoadEnd}
        onError={onError}
        data-testid={testID}
        {...(filterDomProps(props) as HTMLAttributes<HTMLImageElement>)}
      />
    )
  }),
  {
    resolveAssetSource,
    getSize: getImageSize,
  },
)

export type FlatListRef = {
  scrollToIndex: (params: { index: number; animated?: boolean }) => boolean
  scrollToOffset: (params: { offset: number; animated?: boolean }) => void
}

export const FlatList = forwardRef<
  FlatListRef,
  {
    data: unknown[]
    renderItem: (info: { item: unknown; index: number }) => React.ReactNode
    keyExtractor?: (item: unknown, index: number) => string
    style?: unknown
    contentContainerStyle?: unknown
    ListHeaderComponent?: React.ComponentType | React.ReactElement | null
    ListFooterComponent?: React.ComponentType | React.ReactElement | null
    ListEmptyComponent?: React.ComponentType | React.ReactElement | null
    inverted?: boolean
    horizontal?: boolean
    onScroll?: (e: { nativeEvent: { contentOffset: { x: number; y: number } } }) => void
    onScrollBeginDrag?: () => void
    onScrollEndDrag?: () => void
    onEndReached?: () => void
    onEndReachedThreshold?: number
    scrollEventThrottle?: number
    showsVerticalScrollIndicator?: boolean
    showsHorizontalScrollIndicator?: boolean
    extraData?: unknown
    refreshing?: boolean
    viewabilityConfig?: unknown
    maintainVisibleContentPosition?: unknown
    updateCellsBatchingPeriod?: number
    removeClippedSubviews?: boolean
    /** 为 true 时跳过 inverted 列表首帧自动滚到底部（用于定位到历史消息） */
    suppressInitialInvertedScroll?: boolean
  }
>(function FlatList(
  {
    data = [],
    renderItem,
    keyExtractor,
    style,
    contentContainerStyle,
    ListHeaderComponent,
    ListFooterComponent,
    ListEmptyComponent,
    inverted,
    horizontal,
    onScroll,
    onScrollBeginDrag,
    onScrollEndDrag,
    onEndReached,
    onEndReachedThreshold = 0.5,
    showsVerticalScrollIndicator: _showsVerticalScrollIndicator,
    showsHorizontalScrollIndicator: _showsHorizontalScrollIndicator,
    scrollEventThrottle: _scrollEventThrottle,
    extraData: _extraData,
    refreshing: _refreshing,
    viewabilityConfig: _viewabilityConfig,
    maintainVisibleContentPosition: _maintainVisibleContentPosition,
    updateCellsBatchingPeriod: _updateCellsBatchingPeriod,
    removeClippedSubviews: _removeClippedSubviews,
    suppressInitialInvertedScroll = false,
  },
  ref,
) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const endReachedRef = useRef(false)
  const isDraggingRef = useRef(false)
  const scrollMetricsRef = useRef({
    dataLen: data.length,
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
  })

  const firstItemKeyRef = useRef<string | null>(null)

  /** inverted 聊天列表：区分「顶部插入历史」与「底部新消息」，分别保持视口 / 滚到最新 */
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const prev = scrollMetricsRef.current
    const prevFirstKey = firstItemKeyRef.current
    const nextFirstKey =
      data.length > 0 && keyExtractor
        ? String(keyExtractor(data[0], 0))
        : null

    if (inverted && data.length > prev.dataLen && prev.dataLen > 0) {
      const heightDelta = el.scrollHeight - prev.scrollHeight
      if (heightDelta > 0) {
        const wasAtBottom =
          prev.scrollHeight <= prev.clientHeight ||
          prev.scrollTop >= prev.scrollHeight - prev.clientHeight - 2
        const isHistoryGrowth = nextFirstKey != null && nextFirstKey === prevFirstKey
        const isNewMessage = nextFirstKey != null && nextFirstKey !== prevFirstKey

        if (isNewMessage && wasAtBottom) {
          el.scrollTop = el.scrollHeight - el.clientHeight
        } else if (isHistoryGrowth && !wasAtBottom) {
          el.scrollTop = prev.scrollTop + heightDelta
        }
      }
    } else if (inverted && data.length > 0 && prev.dataLen === 0) {
      // 首帧：inverted 列表默认 scrollTop=0 会停在旧消息，先滚到最新（定位历史消息时跳过）
      if (!suppressInitialInvertedScroll) {
        el.scrollTop = Math.max(0, el.scrollHeight - el.clientHeight)
      }
    }

    firstItemKeyRef.current = nextFirstKey
    scrollMetricsRef.current = {
      dataLen: data.length,
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
    }

    // 内容不足一屏时 onScroll 可能永远碰不到 endReached 阈值
    if (onEndReached && data.length > 0) {
      const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight)
      if (maxScroll <= 8) {
        onEndReached()
      }
    }
  }, [data, data.length, inverted, keyExtractor, onEndReached, suppressInitialInvertedScroll])

  const getMaxScrollTop = useCallback(() => {
    const el = scrollRef.current
    if (!el) return 0
    return Math.max(0, el.scrollHeight - el.clientHeight)
  }, [])

  const toDomScrollTop = useCallback(
    (offset: number) => {
      if (!inverted) return offset
      return getMaxScrollTop() - offset
    },
    [getMaxScrollTop, inverted],
  )

  const toInvertedOffset = useCallback(
    (scrollTop: number) => {
      if (!inverted) return scrollTop
      return getMaxScrollTop() - scrollTop
    },
    [getMaxScrollTop, inverted],
  )

  const scrollToDomTop = useCallback((top: number, animated: boolean) => {
    const el = scrollRef.current
    if (!el) return
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight)
    el.scrollTo({ top: Math.max(0, Math.min(top, maxScroll)), behavior: animated ? "smooth" : "auto" })
  }, [])

  const scrollToDataIndex = useCallback(
    (index: number, animated: boolean): boolean => {
      const el = scrollRef.current
      const node = itemRefs.current.get(index)
      if (!el) return false

      const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight)

      if (inverted && index === 0) {
        scrollToDomTop(maxScroll, animated)
        return true
      }

      if (!node) {
        return false
      }

      let targetTop = node.offsetTop
      if (inverted) {
        targetTop = node.offsetTop + node.offsetHeight - el.clientHeight
      }
      scrollToDomTop(targetTop, animated)
      return true
    },
    [inverted, scrollToDomTop],
  )

  useImperativeHandle(
    ref,
    () => ({
      scrollToOffset({ offset, animated = true }) {
        scrollToDomTop(toDomScrollTop(offset), animated)
      },
      scrollToIndex({ index, animated = true }) {
        return scrollToDataIndex(index, animated)
      },
    }),
    [scrollToDataIndex, scrollToDomTop, toDomScrollTop],
  )

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget
      scrollMetricsRef.current = {
        ...scrollMetricsRef.current,
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
      }
      const offsetY = toInvertedOffset(el.scrollTop)
      onScroll?.({ nativeEvent: { contentOffset: { x: el.scrollLeft, y: offsetY } } })

      if (!onEndReached) return

      const thresholdPx = el.clientHeight * onEndReachedThreshold
      const nearVisualTop = inverted ? el.scrollTop <= thresholdPx : el.scrollTop + el.clientHeight >= el.scrollHeight - thresholdPx
      if (nearVisualTop && !endReachedRef.current) {
        endReachedRef.current = true
        onEndReached()
      } else if (!nearVisualTop) {
        endReachedRef.current = false
      }
    },
    [inverted, onEndReached, onEndReachedThreshold, onScroll, toInvertedOffset],
  )

  const handlePointerDown = useCallback(() => {
    isDraggingRef.current = true
    onScrollBeginDrag?.()
  }, [onScrollBeginDrag])

  const handlePointerUp = useCallback(() => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    onScrollEndDrag?.()
  }, [onScrollEndDrag])

  const items = inverted ? [...data].reverse() : data
  const renderSlot = (slot?: React.ComponentType | React.ReactElement | null) => {
    if (!slot) return null
    return React.isValidElement(slot) ? slot : React.createElement(slot)
  }

  const domStyle = useDomStyle(style, {
    overflow: horizontal ? "auto hidden" : "auto",
    WebkitOverflowScrolling: "touch",
    minHeight: 0,
    width: "100%",
    alignSelf: "stretch",
    boxSizing: "border-box",
    ...(horizontal ? { height: "100%" } : { flex: 1 }),
  })
  const contentStyle = useDomStyle(contentContainerStyle, { flexGrow: 1, width: "100%" })

  return (
    <div
      className={horizontal ? "rn-scroll-view rn-scroll-view--horizontal rn-flat-list" : "rn-scroll-view rn-flat-list"}
      ref={scrollRef}
      style={domStyle}
      onScroll={handleScroll}
      onMouseDown={handlePointerDown}
      onMouseUp={handlePointerUp}
      onMouseLeave={handlePointerUp}
      onTouchStart={handlePointerDown}
      onTouchEnd={handlePointerUp}
    >
      <div style={contentStyle}>
        {renderSlot(ListHeaderComponent)}
        {items.length === 0
          ? renderSlot(ListEmptyComponent)
          : items.map((item, displayIndex) => {
              const dataIndex = inverted ? data.length - 1 - displayIndex : displayIndex
              return (
                <div
                  key={keyExtractor?.(item, dataIndex) ?? dataIndex}
                  ref={(node) => {
                    if (node) itemRefs.current.set(dataIndex, node)
                    else itemRefs.current.delete(dataIndex)
                  }}
                >
                  {renderItem({ item, index: dataIndex })}
                </div>
              )
            })}
        {renderSlot(ListFooterComponent)}
      </div>
    </div>
  )
})

type SectionListSection = {
  title?: string
  letter?: string
  label?: string
  data?: unknown[]
  [key: string]: unknown
}

export const SectionList = forwardRef<
  HTMLDivElement,
  {
    sections?: SectionListSection[]
    renderItem: (info: { item: unknown; index: number; section: SectionListSection }) => React.ReactNode
    renderSectionHeader?: (info: { section: SectionListSection }) => React.ReactNode
    keyExtractor?: (item: unknown, index: number) => string
    style?: unknown
    contentContainerStyle?: unknown
    ListHeaderComponent?: React.ComponentType | React.ReactElement | null
    ListFooterComponent?: React.ComponentType | React.ReactElement | null
    ListEmptyComponent?: React.ComponentType | React.ReactElement | null
    ItemSeparatorComponent?: React.ComponentType | React.ReactElement | null
    refreshing?: boolean
    onRefresh?: () => void
    onEndReached?: () => void
    onEndReachedThreshold?: number
    stickySectionHeadersEnabled?: boolean
    showsVerticalScrollIndicator?: boolean
  }
>(function SectionList(
  {
    sections = [],
    renderItem,
    renderSectionHeader,
    keyExtractor,
    style,
    contentContainerStyle,
    ListHeaderComponent,
    ListFooterComponent,
    ListEmptyComponent,
    ItemSeparatorComponent,
    showsVerticalScrollIndicator: _showsVerticalScrollIndicator,
    refreshing: _refreshing,
    onRefresh: _onRefresh,
    onEndReached,
    onEndReachedThreshold = 0.2,
    stickySectionHeadersEnabled: _stickySectionHeadersEnabled,
  },
  ref,
) {
  const endReachedRef = useRef(false)

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!onEndReached) return
      const el = e.currentTarget
      if (el.scrollHeight <= el.clientHeight + 1) return
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
      const nearBottom = distanceFromBottom <= Math.max(48, el.clientHeight * onEndReachedThreshold)
      if (nearBottom) {
        if (!endReachedRef.current) {
          endReachedRef.current = true
          onEndReached()
        }
      } else {
        endReachedRef.current = false
      }
    },
    [onEndReached, onEndReachedThreshold],
  )

  const hasItems = sections.some((section) => (section.data?.length ?? 0) > 0)

  useEffect(() => {
    endReachedRef.current = false
  }, [sections])

  const renderSlot = (slot?: React.ComponentType | React.ReactElement | null) => {
    if (!slot) return null
    return React.isValidElement(slot) ? slot : React.createElement(slot)
  }

  const renderSeparator = () => {
    if (!ItemSeparatorComponent) return null
    return React.isValidElement(ItemSeparatorComponent)
      ? ItemSeparatorComponent
      : React.createElement(ItemSeparatorComponent)
  }

  return (
    <ScrollView ref={ref} style={style} contentContainerStyle={contentContainerStyle} onScroll={handleScroll}>
      {renderSlot(ListHeaderComponent)}
      {!hasItems
        ? renderSlot(ListEmptyComponent)
        : sections.map((section, sectionIndex) => {
            const sectionData = section.data ?? []
            if (sectionData.length === 0) return null
            return (
              <React.Fragment key={section.letter ?? section.title ?? section.label ?? sectionIndex}>
                {renderSectionHeader?.({ section })}
                {sectionData.map((item, index) => (
                  <React.Fragment key={keyExtractor?.(item, index) ?? `${sectionIndex}-${index}`}>
                    {renderItem({ item, index, section })}
                    {ItemSeparatorComponent && index < sectionData.length - 1 ? renderSeparator() : null}
                  </React.Fragment>
                ))}
              </React.Fragment>
            )
          })}
      {renderSlot(ListFooterComponent)}
    </ScrollView>
  )
})

export const ImageBackground = forwardRef<
  HTMLDivElement,
  BaseProps &
    HTMLAttributes<HTMLDivElement> & {
      src?: ImageSrcInput
      /** @deprecated 请使用 src */
      source?: ImageSrcInput
      imageStyle?: unknown
      resizeMode?: "cover" | "contain" | "stretch" | "center"
    }
>(function ImageBackground({ src, source, style, imageStyle, children, resizeMode = "cover", ...props }, ref) {
  void imageStyle
  const uri = getImageHref(src, source)
  const size =
    resizeMode === "contain" ? "contain" : resizeMode === "stretch" ? "100% 100%" : "cover"
  const domStyle = useDomStyle(style, {
    backgroundImage: uri ? `url(${uri})` : undefined,
    backgroundSize: size,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
  })
  return (
    <div ref={ref} style={domStyle} {...(filterDomProps(props) as HTMLAttributes<HTMLDivElement>)}>
      {children}
    </div>
  )
})

export const SafeAreaView = View

export const KeyboardAvoidingView = forwardRef<
  HTMLDivElement,
  BaseProps &
    HTMLAttributes<HTMLDivElement> & {
      behavior?: "padding" | "height" | "position"
      keyboardVerticalOffset?: number
    }
>(function KeyboardAvoidingView(
  { style, children, behavior: _behavior, keyboardVerticalOffset: _offset, ...props },
  ref,
) {
  return (
    <View ref={ref} style={style} {...props}>
      {children}
    </View>
  )
})

export const StatusBar = () => null

export const Modal = ({
  visible,
  children,
  transparent,
  animationType,
}: {
  visible?: boolean
  children?: React.ReactNode
  transparent?: boolean
  animationType?: string
}) => {
  if (!visible) return null
  void transparent
  void animationType
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        flexDirection: "column",
        background: transparent ? "transparent" : "#fff",
      }}
    >
      {children}
    </div>
  )
}

export const RefreshControl = () => null

export const LogBox = {
  ignoreLogs: (_patterns: string[]) => {},
}

export const PixelRatio = {
  get: () => (typeof window !== "undefined" ? window.devicePixelRatio : 1),
  roundToNearestPixel: (value: number) => Math.round(value),
  getFontScale: () => 1,
  getPixelSizeForLayoutSize: (value: number) => value,
}

export const PermissionsAndroid = {
  PERMISSIONS: { POST_NOTIFICATIONS: "POST_NOTIFICATIONS" },
  RESULTS: { GRANTED: "granted", DENIED: "denied" },
  request: async () => "granted",
  check: async () => true,
}

export const Easing = {
  linear: (t: number) => t,
  ease: (t: number) => t,
  quad: (t: number) => t * t,
  cubic: (t: number) => t * t * t,
  circle: (t: number) => t,
  in: (fn: (t: number) => number) => fn,
  out: (fn: (t: number) => number) => fn,
  inOut: (fn: (t: number) => number) => fn,
}

export const LayoutAnimation = {
  configureNext: () => {},
  Presets: { easeInEaseOut: {} },
}

export const findNodeHandle = (node: unknown) => node

export { default as Animated } from "./reanimated"

export const UIManager = {
  measure: (_node: unknown, cb: (...args: number[]) => void) => cb(0, 0, 0, 0, 0, 0),
  measureInWindow: (_node: unknown, cb: (...args: number[]) => void) => cb(0, 0, 0, 0),
}

export const ActionSheetIOS = {
  showActionSheetWithOptions: (
    _options: { options: string[]; cancelButtonIndex?: number },
    callback: (index: number) => void,
  ) => callback(0),
}

export const Button = Pressable

export { WebSwitch as Switch } from "./switch"

export { StyleSheet, flattenStyle } from "./style"
export { Platform } from "./Platform"
export { Dimensions } from "./Dimensions"
export {
  DeviceEventEmitter,
  BackHandler,
  Keyboard,
  Linking,
  Alert,
  AppState,
  type AppStateStatus,
} from "./events"
